<template>
  <div ref="rootRef" class="behaviour-student-lazy-select">
    <CustomSelect
      ref="customSelectRef"
      :input-id="resolvedInputId"
      :accessible-label="accessibleLabel"
      :hide-floating-label="hideFloatingLabel"
      :labelled-by="labelledBy"
      :model-value="modelValue"
      :class="classes"
      :options="visibleOptions"
      :clearable="clearable"
      :placeholder="placeholder || $t('common.select')"
      :filterable="false"
      :autoscroll="false"
      :multiple="multiple"
      :reduce="reduce"
      :selectable="isSelectable"
      :invalid-feedback="invalidFeedback"
      :disabled="disabled"
      :append-dropdown-to-body="appendDropdownToBody"
      :append-dropdown-fixed-position="appendDropdownFixedPosition"
      :close-on-select="!multiple"
      :data-testid="dataTestid"
      @search="onSearch"
      @update:model-value="onChange"
      @open="onOpenSelect"
      @close="onCloseSelect"
    >
      <template #list-footer>
        <div
          v-if="state.isProcessing || showLoadingLabel"
          class="p-2 text-center text-muted small"
        >
          {{ $t("common.loading_more") }}
        </div>
      </template>
      <template #no-options>
        <span class="text-black-50 pm-1 d-block">
          {{ $t("common.sorry_no_matching_options") }}
        </span>
      </template>
    </CustomSelect>
    <output
      v-if="state.isProcessing"
      class="visually-hidden"
      aria-live="polite"
      aria-atomic="true"
    >
      {{ $t("common.loading_more") }}
    </output>
  </div>
</template>

<script>
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import {
  getLazySelectDropdownMenu,
  restoreLazySelectDropdownScroll,
  scheduleLazySelectLoadMore
} from "@/helpers/behaviourLazySelectScroll.js"
import {
  buildStudentSearchParams,
  getSelectedStudentOptions,
  mergeStudentSelectOptions,
  parseStudentSearchResponse
} from "@/helpers/behaviourStudentSelect.js"
import {
  computed,
  getCurrentInstance,
  nextTick,
  onUnmounted,
  reactive,
  ref
} from "vue"
import { useStore } from "vuex"

const SEARCH_DEBOUNCE_MS = 320
const PER_PAGE = 25

export default {
  name: "BehaviourStudentLazySelect",
  components: { CustomSelect },
  props: {
    modelValue: {
      type: [Array, Object, String, Number],
      default: null
    },
    multiple: {
      type: Boolean,
      default: false
    },
    selectOnlyNames: {
      type: Boolean,
      default: false
    },
    showInactive: {
      type: Boolean,
      default: false
    },
    reduce: {
      type: Function,
      default: undefined
    },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    inputId: { type: String, default: "" },
    accessibleLabel: { type: String, default: "" },
    hideFloatingLabel: { type: Boolean, default: false },
    labelledBy: { type: String, default: "" },
    invalidFeedback: { type: [String, Array], default: "" },
    classes: { type: [String, Object, Array], default: "" },
    appendDropdownToBody: { type: Boolean, default: true },
    appendDropdownFixedPosition: { type: Boolean, default: true },
    dataTestid: { type: String, default: "" }
  },
  emits: ["update:modelValue", "changed"],
  setup(props, { emit }) {
    const store = useStore()
    const rootRef = ref(null)
    const customSelectRef = ref(null)
    const instance = getCurrentInstance()

    let detachScroll = null
    let searchTimeoutId = null
    let loadMoreTimeoutId = null

    const state = reactive({
      options: [],
      page: 1,
      lastPage: null,
      searchQuery: "",
      isProcessing: false,
      hasFetched: false
    })

    const resolvedInputId = computed(
      () => props.inputId || `behaviour-student-lazy-${instance?.uid ?? ""}`
    )

    const isLastPage = computed(
      () => state.lastPage != null && state.lastPage <= state.page
    )

    const showLoadingLabel = computed(
      () => state.hasFetched && !isLastPage.value && !state.isProcessing
    )

    const visibleOptions = computed(() =>
      mergeStudentSelectOptions(
        getSelectedStudentOptions(props.modelValue),
        state.options
      )
    )

    const isSelectable = () => true

    const fetchPage = async ({ append = false } = {}) => {
      try {
        state.isProcessing = true
        const response = await store.dispatch(
          "search/searchByTypeAndQuery",
          buildStudentSearchParams({
            page: state.page,
            perPage: PER_PAGE,
            searchQuery: state.searchQuery,
            activeOnly: !props.showInactive
          })
        )
        const { options, lastPage } = parseStudentSearchResponse(
          response,
          props.selectOnlyNames
        )

        if (append) {
          const menu = getLazySelectDropdownMenu(customSelectRef)
          const scrollTop = menu?.scrollTop ?? 0
          const existingIds = new Set(
            state.options
              .map((option) => String(option?.value?.id ?? ""))
              .filter(Boolean)
          )
          const nextOptions = options.filter(
            (option) => !existingIds.has(String(option?.value?.id ?? ""))
          )
          state.options.push(...nextOptions)
          await restoreLazySelectDropdownScroll(customSelectRef, scrollTop)
        } else {
          state.options = options
        }

        state.lastPage = lastPage
        state.hasFetched = true
      } catch {
        if (!append) {
          state.options = []
        }
      } finally {
        state.isProcessing = false
      }
    }

    const onSearch = (query) => {
      const q = query == null ? "" : String(query)
      if (q.replace(/\s+/g, "").length === 1) {
        return
      }

      if (searchTimeoutId) {
        clearTimeout(searchTimeoutId)
      }

      searchTimeoutId = setTimeout(() => {
        state.searchQuery = q
        state.page = 1
        state.lastPage = null
        fetchPage({ append: false })
      }, SEARCH_DEBOUNCE_MS)
    }

    const onChange = (value) => {
      emit("update:modelValue", value)
      emit("changed", value)
    }

    const loadMore = () => {
      scheduleLazySelectLoadMore({
        isBlocked: () => state.isProcessing || isLastPage.value,
        onLoad: () => {
          state.page += 1
          fetchPage({ append: true })
        },
        getTimeoutId: () => loadMoreTimeoutId,
        setTimeoutId: (id) => {
          loadMoreTimeoutId = id
        }
      })
    }

    const handleScroll = (event) => {
      const menu = event.target
      if (!menu || typeof menu.scrollTop !== "number") {
        return
      }
      if (!menu.classList?.contains?.("vs__dropdown-menu")) {
        return
      }
      if (!state.options.length || isLastPage.value || state.isProcessing) {
        return
      }
      if (menu.offsetHeight + menu.scrollTop + 8 >= menu.scrollHeight) {
        loadMore()
      }
    }

    const removeScrollListener = () => {
      if (typeof detachScroll === "function") {
        detachScroll()
      }
      detachScroll = null
    }

    const attachScrollListener = () => {
      removeScrollListener()

      if (props.appendDropdownToBody) {
        nextTick(() => {
          requestAnimationFrame(() => {
            const menu =
              customSelectRef.value?.getDropdownMenuElement?.() || null
            if (menu?.addEventListener) {
              menu.addEventListener("scroll", handleScroll, { passive: true })
              detachScroll = () => {
                menu.removeEventListener("scroll", handleScroll)
              }
            }
          })
        })
        return
      }

      const root = rootRef.value
      if (root?.addEventListener) {
        root.addEventListener("scroll", handleScroll, {
          capture: true,
          passive: true
        })
        detachScroll = () => {
          root.removeEventListener("scroll", handleScroll, { capture: true })
        }
      }
    }

    const onOpenSelect = () => {
      if (props.disabled) {
        return
      }

      if (!state.hasFetched) {
        state.page = 1
        state.lastPage = null
        fetchPage({ append: false })
      }
      attachScrollListener()
    }

    const onCloseSelect = () => {
      removeScrollListener()
    }

    onUnmounted(() => {
      removeScrollListener()
      if (searchTimeoutId) {
        clearTimeout(searchTimeoutId)
      }
      if (loadMoreTimeoutId) {
        clearTimeout(loadMoreTimeoutId)
      }
    })

    return {
      rootRef,
      customSelectRef,
      state,
      resolvedInputId,
      visibleOptions,
      showLoadingLabel,
      isSelectable,
      onSearch,
      onChange,
      onOpenSelect,
      onCloseSelect
    }
  }
}
</script>
